/**
 * activation-incentive — the ONE derivation of "this free account signed up
 * with an attached incentive it hasn't consumed yet", shared by the /welcome
 * discount modal (WelcomeDiscountModal) and the persistent unused-discount
 * banner (UnusedDiscountBanner) so the two surfaces can never disagree about
 * eligibility or the concrete value being advertised.
 *
 * Inputs are the same wire truths the /welcome starter copy already consumes:
 *   - `CRMBillingSummaryVM.starterPromisedCents` (review fix F6) — the
 *     account's ACTUAL unconsumed starter promise, never a blanket
 *     "first month on us" claim.
 *   - `founding.member` off the pro profile — the founding STATUS. Per the
 *     surfaces' spec this is read together with `plan === "free"`.
 *   - `CRMBillingSummaryVM.foundingDiscountMonthsRemaining` — the founding
 *     discount WINDOW. A founder whose window has hit 0 remaining has
 *     lapsed; this surface must fall silent on the founding claim rather
 *     than repeat a "N% off" promise that no longer applies (spec §3.4,
 *     #581). `founding.member` alone is a status flag, not a live window.
 *
 * Returns null whenever there is nothing honest to advertise: billing unknown
 * (loading/error — fail quiet, these are ambient nudges), already subscribed,
 * or no promise + no active founding discount. Starter beats founding for the
 * headline (a concrete dollar credit reads stronger than a percentage), with
 * founding still mentioned in the supporting line when its window is active.
 *
 * The starter/founding branch decision, every rendered "…/mo" figure, and the
 * covered-months count all come from the backend's resolved `activation`
 * block — never the catalog list price, and never arithmetic performed here.
 * See starter-activation-copy.ts and founding-discount.ts for why the money
 * rules live in Go (closes the bd63a8c9 copy regression, and the over-promise
 * that followed it).
 */
import { foundingDiscountPhrase, resolveActivation } from "./founding-discount";
import { money, starterActivationCopy } from "./starter-activation-copy";
import { type CRMBillingSummaryVM } from "./view-model";

export type IncentiveVariant = "starter_full" | "starter_partial" | "founding";

export interface ActivationIncentive {
  /** Which incentive leads — also the analytics `variant` prop (enum-only). */
  variant: IncentiveVariant;
  /** Modal headline naming the concrete value ("$49 credit waiting"). */
  headline: string;
  /** Modal supporting line (price + terms, founding mention when relevant). */
  supporting: string;
  /** Short single-line banner copy; the Activate CTA renders separately. */
  bannerLine: string;
  /**
   * Ultra-compact value label for the post-dismissal header chip
   * ("$49 credit" / "50% off") — the chip has no room for a sentence.
   */
  chipLine: string;
}

export function activationIncentive(
  billing: CRMBillingSummaryVM | null,
  foundingMember: boolean,
): ActivationIncentive | null {
  if (!billing || billing.plan !== "free") return null;

  const promised = billing.starterPromisedCents;
  // The RESOLVED money picture off the wire — percent/months/effective price
  // and how far the credit stretches are all decided by
  // crmbilling.ResolveActivationPricing, never re-derived here. See
  // founding-discount.ts for why this stopped being a client-side derivation.
  const activation = resolveActivation(billing);
  const pct = activation.discountPercent;
  const months = activation.discountMonths;
  const foundingActive = activation.discountActive;
  const foundingLine = `${foundingDiscountPhrase({ percent: pct, months }, foundingMember)}.`;

  if (promised > 0) {
    const starter = starterActivationCopy(activation, promised);
    const supporting = foundingActive
      ? `${starter.body} ${foundingLine}`
      : starter.body;
    // The headline counts WHOLE covered months only; any partial leftover is
    // already spelled out in `starter.body`, which `supporting` carries
    // verbatim.
    const coveredMonths = activation.starterCoversMonths;
    if (activation.effectiveMonthlyCents > 0 && coveredMonths >= 1) {
      const covered =
        coveredMonths === 1
          ? "Your first month is covered"
          : `Your first ${coveredMonths} months are covered`;
      const bannerCovered =
        coveredMonths === 1
          ? "Your first month of Sill Pro is covered"
          : `Your first ${coveredMonths} months of Sill Pro are covered`;
      return {
        variant: "starter_full",
        headline: `${covered} — ${money(promised)} credit waiting`,
        supporting,
        bannerLine: `${bannerCovered} — a ${money(promised)} credit is waiting.`,
        chipLine: `${money(promised)} credit`,
      };
    }
    return {
      variant: "starter_partial",
      headline: `${money(promised)} credit waiting toward your first month`,
      supporting,
      bannerLine: `A ${money(promised)} credit is waiting toward your first month of Sill Pro.`,
      chipLine: `${money(promised)} credit`,
    };
  }

  if (foundingActive) {
    // F2: `months` is the REMAINING months in the window (founding-discount.ts)
    // — reuse `foundingDiscountPhrase`'s unbranded core (foundingMember=false)
    // for the headline so its wording can never drift from the banner/
    // supporting line's phrasing.
    const core = foundingDiscountPhrase({ percent: pct, months }, false);
    return {
      variant: "founding",
      headline: core,
      supporting: `Sill Pro — ${money(activation.effectiveMonthlyCents)}/mo, card required, cancel anytime. ${foundingLine}`,
      bannerLine: `${foundingDiscountPhrase({ percent: pct, months }, foundingMember)} of Sill Pro.`,
      chipLine: `${pct}% off`,
    };
  }

  return null;
}
